/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { CdkMenuModule } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy, Component, Inject, Input, signal,
} from '@angular/core';
import { SocialSharingUtilsService } from '../../common/social-sharing/social-sharing-utils.service';
import { EmojiTextPipe } from '../../pipes/emoji-text.pipe';
import { copyText } from '../copy-text';

// Design-system Share button for the 2026 pages: a menu with Copy link, X and LinkedIn, plus the device's share
// sheet where the browser has one. Replaces the legacy ut-social-sharing icon row.
@Component({
  selector: 'ut-share-menu',
  standalone: true,
  imports: [CdkMenuModule, EmojiTextPipe],
  templateUrl: './share-menu.component.html',
  styleUrls: ['./share-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareMenuComponent {
  @Input({ required: true }) url: string;
  @Input({ required: true }) text: string;

  readonly canUseDeviceShare = typeof navigator !== 'undefined' && !!navigator.share;
  status = signal('');

  constructor(
    private socialSharing: SocialSharingUtilsService,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
  ) {}

  private get shareText(): string {
    return `${this.text} - via @uTest`;
  }

  // Light testers can't share; the service shows them why.
  private blocked(): boolean {
    return !!this.LightTesterErrorService?.show();
  }

  copyLink(): void {
    if (this.blocked()) return;
    // Cleared first, so a second copy is announced again.
    this.status.set('');
    copyText(this.url).then((ok) => setTimeout(() => this.status.set(ok ? 'Link copied' : 'Couldn\'t copy the link')));
  }

  shareOn(network: 'x' | 'linkedin'): void {
    if (this.blocked()) return;
    this.socialSharing.openSocialPopup(network === 'x'
      ? this.socialSharing.getTwitterUrl(this.shareText, this.url)
      : this.socialSharing.getLinkedInUrl(this.shareText, this.url));
  }

  deviceShare(): void {
    if (this.blocked()) return;
    navigator.share({ title: this.text, url: this.url }).catch(() => {});
  }
}
